import { useMemo } from 'react';
import { ApolloClient, HttpLink, ApolloLink, from, InMemoryCache } from '@apollo/client';
import merge from 'deepmerge';
import fetch from 'node-fetch';
import { configService } from '@rakutenfrance/config-service';
import { FALLBACK_LANGUAGE } from '@rakutenfrance/tolgee-provider';
const config = configService.get();
const HOST = typeof window === 'undefined' ? config.backendTechUrl : '';
const URI = `${HOST}/restpublic/tech-web/graphql/common`;
// const URI = 'https://faker.graphqleditor.com/rakutenfr/techws/graphql';
// TODO: Replace the uri with the future good one
const HOST_RFN = typeof window === 'undefined' ? config.backendRfnUrl : '';
const RFN_URI = `${HOST_RFN}/graphql`;
const HOST_TRANSLATION = typeof window === 'undefined' ? config.translationProxyBackendTechUrl : '';
const TRANSLATION_URI = `${HOST_TRANSLATION}/restpublic/tech-web/graphql/common`;
let apolloClient;
const defaultLink = new HttpLink({
    uri: URI, // Server URL (must be absolute)
    credentials: 'same-origin', // Additional fetch() options like `credentials` or `headers`
    fetch: fetch,
});
const rfnLink = new HttpLink({
    uri: RFN_URI, // Server URL (must be absolute)
    credentials: 'same-origin', // Additional fetch() options like `credentials` or `headers`
    fetch: fetch,
});
const translationLink = new HttpLink({
    uri: TRANSLATION_URI,
    credentials: 'same-origin',
    fetch: fetch,
});
const responseHeader = (options) => new ApolloLink((operation, forward) => forward(operation).map((response) => {
    const { response: { headers }, } = operation.getContext();
    if (options && options.onResponseHeader)
        options.onResponseHeader(headers);
    return response;
}));
function createApolloClient(options) {
    const cache = new InMemoryCache({
        typePolicies: {
            Profile: {
                keyFields: [],
            },
            SuperPointBalance: {
                keyFields: [],
            },
            Club: {
                keyFields: [],
            },
        },
    });
    // Create the link chain with multiple splits
    const link = ApolloLink.split(
    // First split: Check if it's an RFN request
    (operation) => operation.getContext().clientName === 'RFN', 
    // If RFN, use RFN link
    from([responseHeader(options), rfnLink]), 
    // If not RFN, check for translation
    ApolloLink.split((operation) => {
        const userLanguage = operation.getContext().userLanguage;
        if (userLanguage) {
            return userLanguage !== FALLBACK_LANGUAGE;
        }
        return false;
    }, 
    // If non-FR language, use translation link
    from([responseHeader(options), translationLink]), 
    // Otherwise use default link
    from([responseHeader(options), defaultLink])));
    return new ApolloClient({
        ssrMode: typeof window === 'undefined',
        link,
        cache,
    });
}
function isEqual(obj1, obj2) {
    function areArraysEqual() {
        if (obj1.length !== obj2.length)
            return false;
        for (let i = 0; i < obj1.length; i++) {
            if (!isEqual(obj1[i], obj2[i]))
                return false;
        }
        return true;
    }
    function areObjectsEqual() {
        if (Object.keys(obj1).length !== Object.keys(obj2).length)
            return false;
        for (const key in obj1) {
            if (Object.prototype.hasOwnProperty.call(obj1, key)) {
                if (!isEqual(obj1[key], obj2[key]))
                    return false;
            }
        }
        return true;
    }
    function areFunctionsEqual() {
        return obj1.toString() === obj2.toString();
    }
    function arePrimitivesEqual() {
        return obj1 === obj2;
    }
    function getType(obj) {
        return Object.prototype.toString.call(obj).slice(8, -1).toLowerCase();
    }
    const type = getType(obj1);
    if (type !== getType(obj2))
        return false;
    if (type === 'array')
        return areArraysEqual();
    if (type === 'object')
        return areObjectsEqual();
    if (type === 'function')
        return areFunctionsEqual();
    return arePrimitivesEqual();
}
export function initializeApollo(initialState = null, options) {
    let myApolloClient;
    apolloClient ? (myApolloClient = apolloClient) : (myApolloClient = createApolloClient(options));
    // If your page has Next.js data fetching methods that use Apollo Client, the initial state
    // gets hydrated here
    if (initialState) {
        // myApolloClient.cache.restore(initialState);
        // Get existing cache, loaded during client side data fetching
        const existingCache = myApolloClient.extract();
        // Merge the existing cache into data passed from getStaticProps/getServerSideProps
        const data = merge(initialState, existingCache, {
            // combine arrays using object equality (like in sets)
            arrayMerge: (destinationArray, sourceArray) => [
                ...sourceArray,
                ...destinationArray.filter((d) => sourceArray.every((s) => !isEqual(d, s))),
            ],
        });
        // Restore the cache with the merged data
        myApolloClient.cache.restore(data);
    }
    // For SSG and SSR always create a new Apollo Client
    if (typeof window === 'undefined')
        return myApolloClient;
    // Create the Apollo Client once in the client
    if (!apolloClient)
        apolloClient = myApolloClient;
    return myApolloClient;
}
export function useApollo(initialState) {
    const store = useMemo(() => initializeApollo(initialState), [initialState]);
    return store;
}
